import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HttpStatusCode } from 'axios';
import { shallowEqual, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ROUTES_PATH } from 'app/constants/Routes';
import {
  trackPasswordCreationModalAttemptStarted,
  trackPasswordCreationModalDismissed,
  trackPasswordCreationModalDisplayed,
  trackPasswordCreationModalSuccessful,
} from 'app/modules/Auth/trackPasswordEvents';
import {
  createFirstPassword,
  type CredentialsStatus,
  getCredentialsStatus,
} from 'app/services/credentials';
import { getErrorStatus, getFieldError, getServerMessage } from './helpers';
import { FIRST_PASSWORD_RULES } from './rules';
import { FIRST_PASSWORD_MODAL_TEXTS } from './texts';
import type {
  FirstPasswordModalState,
  UseFirstPasswordModalInput,
  UseFirstPasswordModalResult,
} from './types';

export const credentialsStatusQueryKey = (userId?: number) =>
  ['credentials-status', userId] as const;

export function useFirstPasswordModal({
  isAnotherModalOpen,
}: UseFirstPasswordModalInput): UseFirstPasswordModalResult {
  const {
    userId,
    userEmail,
    isOfferModalOpen,
    isPlanLimitModalOpen,
  } = useSelector(
    (state: FirstPasswordModalState) => ({
      userId: state.auth?.user?.id,
      userEmail: state.auth?.user?.email ?? '',
      isOfferModalOpen: Boolean(state.app?.is_modal_offer_open),
      isPlanLimitModalOpen: Boolean(state.app?.modal_subscription?.visible),
    }),
    shallowEqual,
  );
  const isOnDashboard = Boolean(
    useRouteMatch({ path: ROUTES_PATH.MAIN.DASHBOARD, exact: true }),
  );
  const queryClient = useQueryClient();

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [isConfirmationTouched, setIsConfirmationTouched] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const hasTrackedDisplayed = useRef(false);

  const queryKey = credentialsStatusQueryKey(userId);

  const { data } = useQuery({
    queryKey,
    queryFn: getCredentialsStatus,
    enabled: isOnDashboard,
    staleTime: Infinity,
    retry: false,
  });

  const resetForm = () => {
    setPassword('');
    setPasswordError('');
    setConfirmation('');
    setIsConfirmationTouched(false);
  };

  const markPasswordCreated = () => {
    queryClient.setQueryData<CredentialsStatus>(queryKey, (current) => ({
      ...current,
      passwordSetupRequired: false,
    }));
    resetForm();
  };

  const mutation = useMutation({
    mutationFn: createFirstPassword,
    onSuccess: () => {
      markPasswordCreated();
      toast.success(FIRST_PASSWORD_MODAL_TEXTS.success);
      trackPasswordCreationModalSuccessful();
    },
    onError: (error) => {
      const status = getErrorStatus(error);
      const serverMessage = getServerMessage(error);
      const passwordFieldError = getFieldError(error, 'password');

      if (status === HttpStatusCode.Conflict) {
        markPasswordCreated();
        toast.info(FIRST_PASSWORD_MODAL_TEXTS.alreadyCreated);
        return;
      }

      if (status === HttpStatusCode.BadRequest && serverMessage) {
        setPasswordError(serverMessage);
        return;
      }

      if (status === HttpStatusCode.UnprocessableEntity && passwordFieldError) {
        setPasswordError(passwordFieldError);
        return;
      }

      toast.error(serverMessage ?? FIRST_PASSWORD_MODAL_TEXTS.genericError);
    },
  });

  const isSubmitting = mutation.isLoading;
  const isBlockedByAnotherModal =
    isAnotherModalOpen || isOfferModalOpen || isPlanLimitModalOpen;
  const isOpen =
    isOnDashboard &&
    data?.passwordSetupRequired === true &&
    !isDismissed &&
    !isBlockedByAnotherModal;
  const isPasswordValid = FIRST_PASSWORD_RULES.every((rule) =>
    rule.test(password),
  );
  const hasMatchingConfirmation = confirmation === password;
  const confirmationError =
    isConfirmationTouched && confirmation && !hasMatchingConfirmation
      ? FIRST_PASSWORD_MODAL_TEXTS.mismatch
      : '';
  const canSubmit = isPasswordValid && Boolean(confirmation) && !isSubmitting;

  useEffect(() => {
    if (!isOpen || hasTrackedDisplayed.current) return;
    hasTrackedDisplayed.current = true;
    trackPasswordCreationModalDisplayed();
  }, [isOpen]);

  const changePassword = (value: string) => {
    setPassword(value);
    setPasswordError('');
  };

  const changeConfirmation = (value: string) => {
    setConfirmation(value);
  };

  const blurConfirmation = () => {
    setIsConfirmationTouched(true);
  };

  const submit = () => {
    setIsConfirmationTouched(true);
    if (!canSubmit || !hasMatchingConfirmation) return;
    trackPasswordCreationModalAttemptStarted();
    mutation.mutate(password);
  };

  const dismiss = () => {
    if (isSubmitting) return;
    setIsDismissed(true);
    resetForm();
    trackPasswordCreationModalDismissed();
  };

  return {
    isOpen,
    userEmail,
    password,
    passwordError,
    confirmation,
    confirmationError,
    isSubmitting,
    canSubmit,
    changePassword,
    changeConfirmation,
    blurConfirmation,
    submit,
    dismiss,
  };
}
